import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

const TimeCodePage = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [serverCode, setServerCode] = useState('');

  // Generate time-based code (1-minute intervals) + 4 dummy digits
  const generateTimeCode = () => {
    const now = new Date();
    const totalMinutes = Math.floor(now.getTime() / (1 * 60 * 1000));
    const baseCode = String((totalMinutes * 7919) % 1000000).padStart(6, '0');
    
    // Add 4 deterministic dummy digits based on base code
    const dummyDigits = String((parseInt(baseCode.slice(0, 3)) * 37) % 10000).padStart(4, '0');
    
    return baseCode + dummyDigits; // 10 digits total
  };

  // Get time for different zones (frozen on page load)
  const getWorldTimes = (baseTime) => {
    const berlin = new Date(baseTime.toLocaleString("en-US", {timeZone: "Europe/Berlin"}));
    const newYork = new Date(baseTime.toLocaleString("en-US", {timeZone: "America/New_York"}));
    const tokyo = new Date(baseTime.toLocaleString("en-US", {timeZone: "Asia/Tokyo"}));
    const london = new Date(baseTime.toLocaleString("en-US", {timeZone: "Europe/London"}));
    const sydney = new Date(baseTime.toLocaleString("en-US", {timeZone: "Australia/Sydney"}));

    return {
      berlin: berlin.toLocaleTimeString('de-DE', { hour12: false }),
      newYork: newYork.toLocaleTimeString('en-US', { hour12: false }),
      tokyo: tokyo.toLocaleTimeString('ja-JP', { hour12: false }),
      london: london.toLocaleTimeString('en-GB', { hour12: false }),
      sydney: sydney.toLocaleTimeString('en-AU', { hour12: false })
    };
  };

  useEffect(() => {
    // SEO protection
    const metaNoIndex = document.createElement('meta');
    metaNoIndex.name = 'robots';
    metaNoIndex.content = 'noindex, nofollow, noarchive, nosnippet';
    document.head.appendChild(metaNoIndex);

    // Set times on page load (frozen)
    const loadTime = new Date();
    setCurrentTime(loadTime);
    setServerCode(generateTimeCode());

    // Cleanup
    return () => {
      document.head.removeChild(metaNoIndex);
    };
  }, []);

  const worldTimes = getWorldTimes(currentTime);
  const currentDate = currentTime.toLocaleDateString('de-DE', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <Clock className="w-8 h-8 text-blue-600 mr-3" />
              <h1 className="text-3xl font-bold text-gray-900">Internationale Uhrzeiten</h1>
            </div>
            <p className="text-gray-600 text-lg">{currentDate}</p>
          </div>

          {/* World Times Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            
            {/* Berlin */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-center mb-3">
                <div className="w-4 h-4 bg-red-500 rounded-full mr-3"></div>
                <h3 className="text-lg font-semibold text-gray-800">Berlin</h3>
              </div>
              <div className="text-3xl font-mono text-gray-900">{worldTimes.berlin}</div>
              <div className="text-sm text-gray-500 mt-1">MEZ/MESZ</div>
            </div>

            {/* New York */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-center mb-3">
                <div className="w-4 h-4 bg-blue-500 rounded-full mr-3"></div>
                <h3 className="text-lg font-semibold text-gray-800">New York</h3>
              </div>
              <div className="text-3xl font-mono text-gray-900">{worldTimes.newYork}</div>
              <div className="text-sm text-gray-500 mt-1">EST/EDT</div>
            </div>

            {/* Tokyo */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-center mb-3">
                <div className="w-4 h-4 bg-red-600 rounded-full mr-3"></div>
                <h3 className="text-lg font-semibold text-gray-800">Tokyo</h3>
              </div>
              <div className="text-3xl font-mono text-gray-900">{worldTimes.tokyo}</div>
              <div className="text-sm text-gray-500 mt-1">JST</div>
            </div>

            {/* London */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-center mb-3">
                <div className="w-4 h-4 bg-blue-800 rounded-full mr-3"></div>
                <h3 className="text-lg font-semibold text-gray-800">London</h3>
              </div>
              <div className="text-3xl font-mono text-gray-900">{worldTimes.london}</div>
              <div className="text-sm text-gray-500 mt-1">GMT/BST</div>
            </div>

            {/* Sydney */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-center mb-3">
                <div className="w-4 h-4 bg-green-600 rounded-full mr-3"></div>
                <h3 className="text-lg font-semibold text-gray-800">Sydney</h3>
              </div>
              <div className="text-3xl font-mono text-gray-900">{worldTimes.sydney}</div>
              <div className="text-sm text-gray-500 mt-1">AEST/AEDT</div>
            </div>

            {/* Server Status - THE HIDDEN CODE (unauffällig) */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-center mb-3">
                <div className="w-4 h-4 bg-gray-400 rounded-full mr-3"></div>
                <h3 className="text-lg font-semibold text-gray-800">Server Info</h3>
              </div>
              <div className="text-sm text-gray-600 mb-1">Build-Version:</div>
              <div className="text-lg text-gray-700 mb-2 font-mono tracking-wider">{serverCode}</div>
              <div className="text-sm text-gray-500">System läuft stabil</div>
            </div>
          </div>

          {/* Footer Info */}
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-2xl font-bold text-gray-900 mb-1">UTC</div>
                <div className="text-sm text-gray-600">Koordinierte Weltzeit</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600 mb-1">Online</div>
                <div className="text-sm text-gray-600">Alle Zeitzonen verfügbar</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600 mb-1">24/7</div>
                <div className="text-sm text-gray-600">Kontinuierlicher Service</div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TimeCodePage;